import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  border-left: 4px solid
    ${(props) => {
      if (props.originalCategory === "GUARANTEED_VEGAN") return "#2E7D32";
      if (props.originalCategory === "PROBABLY_VEGAN") return "#4CAF50";
      if (props.originalCategory === "MAYBE_VEGAN") return "#FFC107";
      return "#d32f2f";
    }};
  position: relative;
  z-index: ${(props) => (props.isDropdownOpen ? 1000 : 1)};

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  ${(props) =>
    props.userChanged &&
    `
    &::after {
      content: "✏️";
      position: absolute;
      top: 8px;
      right: 8px;
      font-size: 14px;
      opacity: 0.7;
    }
  `}
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
`;

const OptionNameContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionName = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: #212121;
  cursor: ${(props) => (props.editable ? "pointer" : "default")};
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 4px;
  transition: background-color 0.2s;

  ${(props) =>
    props.editable &&
    `
    &:hover {
      background-color: #f5f5f5;
    }
  `}
`;

const OptionNameInput = styled.input`
  font-weight: bold;
  font-size: 16px;
  color: #212121;
  border: 1px solid #4CAF50;
  border-radius: 4px;
  padding: 2px 4px;
  width: 100%;
  outline: none;
  box-sizing: border-box;

  &:focus {
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const OptionDescription = styled.div`
  font-size: 14px;
  color: #757575;
  margin-bottom: 12px;
  cursor: ${(props) => (props.editable ? "pointer" : "default")};
  padding: 2px 4px;
  margin-left: -4px;
  margin-right: -4px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-height: 20px;

  ${(props) =>
    props.editable &&
    `
    &:hover {
      background-color: #f5f5f5;
    }
  `}

  ${(props) =>
    props.editable &&
    !props.hasContent &&
    `
    color: #bdbdbd;
    font-style: italic;
  `}
`;

const OptionDescriptionInput = styled.textarea`
  font-size: 14px;
  color: #757575;
  border: 1px solid #4CAF50;
  border-radius: 4px;
  padding: 4px;
  width: 100%;
  min-height: 60px;
  outline: none;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
  }
`;

const SectionDropdownContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SectionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 12px;
  color: #757575;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: #eeeeee;
    border-color: #bdbdbd;
  }

  &:focus {
    outline: 2px solid #4CAF50;
    outline-offset: 2px;
  }

  i {
    flex-shrink: 0;
  }
`;

const SectionDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 200px;
  max-height: 300px;
  overflow-y: auto;
`;

const SectionSearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #9e9e9e;
  }
`;

const SectionOption = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.15s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #f5f5f5;
  }

  ${(props) =>
    props.isSelected &&
    `
    background-color: rgba(76, 175, 80, 0.1);
    font-weight: 500;
  `}

  ${(props) =>
    props.isCreate &&
    `
    color: #4CAF50;
    font-weight: 500;
    border-top: 1px solid #e0e0e0;
  `}

  i {
    color: #4CAF50;
  }
`;

const NoSectionOption = styled(SectionOption)`
  color: #9e9e9e;
  font-style: italic;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #9e9e9e;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;

  &:hover {
    background: #ffebee;
    color: #d32f2f;
  }

  &:focus {
    outline: 2px solid #d32f2f;
    outline-offset: 2px;
  }
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
  background: ${(props) => (props.active ? "#2E7D32" : "#F5F5F5")};
  color: ${(props) => (props.active ? "white" : "#212121")};
  font-weight: ${(props) => (props.active ? "500" : "400")};

  &:hover {
    background: ${(props) => (props.active ? "#1B5E20" : "#E0E0E0")};
    border-color: ${(props) => (props.active ? "#1B5E20" : "#BDBDBD")};
  }

  &:focus {
    outline: 2px solid #4CAF50;
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.98);
  }
`;

// Helper to extract section name from subdocument or string
const getSectionName = (section) => {
  if (!section) return null;
  if (typeof section === "string") return section;
  return section.name || null;
};

const MenuOptionCard = ({
  option,
  description,
  section,
  originalCategory,
  currentCategory,
  probability,
  onCategoryChange,
  onOptionChange,
  onSectionChange,
  availableSections = [],
  editable = false,
  onDelete,
  source,
  sourceUrl,
  urlIndexMap,
  veganLegend,
}) => {
  const [isEditingName, setIsEditingName] = useState(editable && !option);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState(option);
  const [editedDescription, setEditedDescription] = useState(description || "");
  const [isSectionDropdownOpen, setIsSectionDropdownOpen] = useState(false);
  const [sectionSearchText, setSectionSearchText] = useState("");

  const nameInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const userChanged = currentCategory !== "maybeVegan";
  const currentSectionName = getSectionName(section);

  // Focus input when entering edit mode
  useEffect(function focusNameInputOnEdit() {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  useEffect(function focusDescriptionInputOnEdit() {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isEditingDescription]);

  // Close dropdown when clicking outside
  useEffect(function closeSectionDropdownOnClickOutside() {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsSectionDropdownOpen(false);
        setSectionSearchText("");
      }
    };

    if (isSectionDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSectionDropdownOpen]);

  const handleCategoryChange = (newCategory) => {
    if (onCategoryChange) {
      onCategoryChange(option, newCategory, originalCategory);
    }
  };

  const handleNameSave = () => {
    setIsEditingName(false);
    if (editedName.trim() && editedName !== option && onOptionChange) {
      onOptionChange(option, { option: editedName.trim() });
    } else {
      setEditedName(option); // Reset if empty or unchanged
    }
  };

  const handleDescriptionSave = () => {
    setIsEditingDescription(false);
    if (editedDescription !== description && onOptionChange) {
      onOptionChange(option, { description: editedDescription });
    }
  };

  const handleSectionSelect = (newSection) => {
    setIsSectionDropdownOpen(false);
    setSectionSearchText("");
    if (onSectionChange && newSection !== currentSectionName) {
      onSectionChange(option, newSection);
    }
  };

  // Filter sections based on search text
  const filteredSections = availableSections.filter(
    (s) => s && s.toLowerCase().includes(sectionSearchText.toLowerCase())
  );

  // Check if search text is a new section name
  const isNewSection =
    sectionSearchText.trim() &&
    !availableSections.some(
      (s) => s && s.toLowerCase() === sectionSearchText.toLowerCase()
    );

  return (
    <Card originalCategory={originalCategory} userChanged={userChanged} isDropdownOpen={isSectionDropdownOpen}>
      <OptionHeader>
        <OptionNameContainer>
          {isEditingName ? (
            <OptionNameInput
              ref={nameInputRef}
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleNameSave();
                if (e.key === "Escape") {
                  setEditedName(option);
                  setIsEditingName(false);
                }
              }}
            />
          ) : (
            <OptionName
              editable={editable}
              onClick={() => editable && setIsEditingName(true)}
              title={editable ? "Click to edit" : undefined}
              style={editable && !option ? { color: "#bdbdbd", fontStyle: "italic" } : undefined}
            >
              {option || (editable ? "Click to add name" : "")}
              {source && source.startsWith("vegan_marker_") && (() => {
                const marker = source.replace("vegan_marker_", "");
                const legendEntry = veganLegend?.find((e) => e.marker === marker);
                const meaning = legendEntry?.meaning || "Vegan";
                return (
                  <span
                    style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      background: "#E8F5E9", color: "#2E7D32", borderRadius: "10px",
                      padding: "2px 6px", fontSize: "11px", fontWeight: "bold",
                      marginLeft: "6px", flexShrink: 0, border: "1px solid #C8E6C9",
                      lineHeight: "16px", whiteSpace: "nowrap",
                    }}
                    title={`Marked "${meaning}" on menu (${marker})`}
                  >
                    {marker}
                  </span>
                );
              })()}
              {source === "fully_vegan_restaurant" && (
                <span
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: "#E8F5E9", color: "#2E7D32", borderRadius: "10px",
                    padding: "2px 6px", fontSize: "11px", fontWeight: "bold",
                    marginLeft: "6px", flexShrink: 0, border: "1px solid #C8E6C9",
                    lineHeight: "16px", whiteSpace: "nowrap",
                  }}
                  title="100% vegan restaurant"
                >
                  ✓
                </span>
              )}
              {sourceUrl && urlIndexMap && urlIndexMap[sourceUrl] && (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={sourceUrl}
                  onClick={(e) => e.stopPropagation()}
                  style={{ fontSize: "11px", color: "#9e9e9e", marginLeft: "4px", fontWeight: "normal", textDecoration: "none", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#1976d2"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "#9e9e9e"; }}
                >
                  [{urlIndexMap[sourceUrl]}]
                </a>
              )}
            </OptionName>
          )}
        </OptionNameContainer>

        {editable && onSectionChange && (
          <SectionDropdownContainer ref={dropdownRef}>
            <SectionButton
              type="button"
              onClick={() => setIsSectionDropdownOpen(!isSectionDropdownOpen)}
              title={currentSectionName || "No section - click to assign"}
            >
              <i className="fas fa-folder" />
              <span>{currentSectionName || "No section"}</span>
              <i
                className={`fas fa-chevron-${isSectionDropdownOpen ? "up" : "down"}`}
              />
            </SectionButton>

            {isSectionDropdownOpen && (
              <SectionDropdown>
                <SectionSearchInput
                  type="text"
                  placeholder="Search or create section..."
                  value={sectionSearchText}
                  onChange={(e) => setSectionSearchText(e.target.value)}
                  autoFocus
                />

                <NoSectionOption
                  isSelected={!currentSectionName}
                  onClick={() => handleSectionSelect(null)}
                >
                  No section
                </NoSectionOption>

                {filteredSections.map((sectionName) => (
                  <SectionOption
                    key={sectionName}
                    isSelected={sectionName === currentSectionName}
                    onClick={() => handleSectionSelect(sectionName)}
                  >
                    {sectionName}
                  </SectionOption>
                ))}

                {isNewSection && (
                  <SectionOption
                    isCreate
                    onClick={() => handleSectionSelect(sectionSearchText.trim())}
                  >
                    <i className="fas fa-plus" />
                    Create "{sectionSearchText.trim()}"
                  </SectionOption>
                )}
              </SectionDropdown>
            )}
          </SectionDropdownContainer>
        )}

        {editable && onDelete && (
          <DeleteButton
            type="button"
            onClick={() => onDelete(option)}
            title="Delete item"
          >
            <i className="fas fa-trash" />
          </DeleteButton>
        )}
      </OptionHeader>

      {isEditingDescription ? (
        <OptionDescriptionInput
          ref={descriptionInputRef}
          value={editedDescription}
          onChange={(e) => setEditedDescription(e.target.value)}
          onBlur={handleDescriptionSave}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setEditedDescription(description || "");
              setIsEditingDescription(false);
            }
          }}
          placeholder="Add a description..."
        />
      ) : (
        <OptionDescription
          editable={editable}
          hasContent={!!description}
          onClick={() => editable && setIsEditingDescription(true)}
          title={editable ? "Click to edit" : undefined}
        >
          {description || (editable ? "Click to add description" : "")}
        </OptionDescription>
      )}

      <ToggleButtonGroup>
        <ToggleButton
          active={currentCategory === "vegan"}
          onClick={() => handleCategoryChange("vegan")}
          type="button"
        >
          Vegan
        </ToggleButton>
        <ToggleButton
          active={currentCategory === "maybeVegan"}
          onClick={() => handleCategoryChange("maybeVegan")}
          type="button"
        >
          Maybe
        </ToggleButton>
        <ToggleButton
          active={currentCategory === "notVegan"}
          onClick={() => handleCategoryChange("notVegan")}
          type="button"
        >
          Not Vegan
        </ToggleButton>
      </ToggleButtonGroup>
    </Card>
  );
};

export default MenuOptionCard;
